/** Wall-mounted wash-down hose reel; the exposed coils keep it from reading as a fan or cable spool. */
import * as THREE from 'three';
import { surface } from '../materials';
import { seatOnOrigin } from '../origin';
import type { AssetDefinition } from '../types';

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'hose-reel';
  const steel = surface('metal', 0x778384);
  const darkSteel = surface('metal', 0x4f5b5c);
  // Rubber needs the non-ageing organic response, just as the nozzle grip and
  // pressure mat do: metal would catch the turnaround light and turn the wound
  // hose back into the polished barrel this asset must not resemble.
  const hose = surface('organic', 0x283334);

  const plate = new THREE.Mesh(new THREE.BoxGeometry(0.52, 0.52, 0.03), darkSteel);
  plate.name = 'wall-mounting-plate';
  plate.position.set(0, 0.26, -0.055);
  plate.castShadow = true;
  group.add(plate);

  for (const x of [-0.15, 0.15]) {
    const arm = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.24, 0.12), steel);
    arm.name = x < 0 ? 'left-drum-bracket' : 'right-drum-bracket';
    arm.position.set(x, 0.26, 0.0);
    arm.castShadow = true;
    group.add(arm);
  }

  const drum = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 0.3, 8), darkSteel);
  drum.name = 'drum-hub';
  drum.rotation.z = Math.PI / 2;
  drum.position.set(0, 0.29, 0.1);
  drum.castShadow = true;
  group.add(drum);

  for (const x of [-0.17, 0.17]) {
    const cheek = new THREE.Mesh(new THREE.CylinderGeometry(0.165, 0.165, 0.02, 10), steel);
    cheek.name = x < 0 ? 'left-cheek-plate' : 'right-cheek-plate';
    cheek.rotation.z = Math.PI / 2;
    cheek.position.set(x, 0.29, 0.1);
    cheek.castShadow = true;
    group.add(cheek);
  }

  // A wound hose is visibly lumpy rather than a lathed cylinder. These six
  // deliberately uneven turns span the hub's width, leaving rubber ridges that
  // survive the dim corridor turnaround instead of merging into a cable drum.
  for (const [x, radius, index] of [
    [-0.125, 0.164, 1],
    [-0.075, 0.18, 2],
    [-0.025, 0.171, 3],
    [0.025, 0.19, 4],
    [0.075, 0.174, 5],
    [0.125, 0.185, 6],
  ] as const) {
    const coil = new THREE.Mesh(new THREE.TorusGeometry(radius, 0.013, 4, 8), hose);
    coil.name = `wound-hose-coil-${index}`;
    coil.rotation.y = Math.PI / 2;
    coil.position.set(x, 0.29, 0.1);
    coil.castShadow = true;
    group.add(coil);
  }

  const crankArm = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.022, 0.022), steel);
  crankArm.name = 'crank-arm';
  crankArm.position.set(0.215, 0.29, 0.1);
  group.add(crankArm);
  const crankHandle = new THREE.Mesh(new THREE.CylinderGeometry(0.014, 0.014, 0.1, 6), darkSteel);
  crankHandle.name = 'crank-handle';
  crankHandle.position.set(0.246, 0.34, 0.1);
  crankHandle.castShadow = true;
  group.add(crankHandle);

  const trailingHose = new THREE.CatmullRomCurve3([
    new THREE.Vector3(0, 0.14, 0.15),
    new THREE.Vector3(0, 0.095, 0.17),
    new THREE.Vector3(0.015, 0.045, 0.21),
    new THREE.Vector3(0.02, 0.008, 0.25),
  ]);
  const tail = new THREE.Mesh(new THREE.TubeGeometry(trailingHose, 10, 0.008, 4, false), hose);
  tail.name = 'hose-to-floor';
  tail.castShadow = true;
  group.add(tail);

  // Authored from a convenient face outward, then seated on the contract's
  // origin. See `seatOnOrigin` for why five assets in this batch needed it.
  seatOnOrigin(group);

  return group;
}

export const hoseReelAsset: AssetDefinition = {
  id: 'hose-reel',
  label: 'Wash-down hose reel',
  description: 'Wall-mounted steel reel with visibly wound hose, crank, and a short floorward tail.',
  footprint: { width: 0.52, height: 0.52, length: 0.406 },
  create: build,
};
